/**
 * V4 Product Page — JavaScript
 *
 * Handles:
 * 1. Image gallery (desktop click-to-switch, mobile scroll carousel + dots)
 * 2. Color swatch selection (visual state + triggers theme.js variant logic)
 * 3. Cart form AJAX submission (intercepts V4 form, dispatches ajaxProduct:added
 *    so theme.CartDrawer opens the drawer with updated contents)
 * 4. Upsell "Add" button (AJAX cart add + ajaxProduct:added dispatch)
 * 5. Mobile gallery scroll tracking for dot indicators
 *
 * Does NOT handle:
 * - Variant switching logic (theme.js does this via [data-variant-input] listeners)
 * - Price updates (theme.js handles via variantChange events)
 * - Cart drawer rendering (theme.CartDrawer handles via ajaxProduct:added event)
 * - Accordion open/close (native <details> elements handle this)
 * - Size recommender breed lookup (psr-tools.js handles this via #SingleOptionBreed)
 */

(function() {
  'use strict';

  /* ==========================================================
     0. SECTION SCOPING HELPER
     Walk up from any element to its V4 PDP container so all
     queries can be scoped to one PDP instance (matters when
     multiple PDPs share a page — modals, quick-view, etc).
     ========================================================== */

  function getV4Section(el) {
    return el && el.closest ? el.closest('.v4-pdp') : null;
  }

  function getAllV4Sections() {
    return document.querySelectorAll('.v4-pdp');
  }


  /* ==========================================================
     1. IMAGE GALLERY
     ========================================================== */

  function initGallery() {
    var galleries = document.querySelectorAll('[data-v4-gallery]');
    galleries.forEach(function(gallery) {
      initSingleGallery(gallery);
    });
  }

  function initSingleGallery(gallery) {
    var slides = gallery.querySelectorAll('[data-v4-slide]');
    var thumbs = gallery.querySelectorAll('[data-v4-thumb]');
    var dots = gallery.querySelectorAll('[data-v4-dot]');
    var slidesContainer = gallery.querySelector('[data-v4-gallery-slides]');

    if (!slides.length) return;

    // Desktop: click thumbnail to switch main image
    thumbs.forEach(function(thumb) {
      thumb.addEventListener('click', function() {
        var index = parseInt(thumb.dataset.index, 10);
        goToSlide(gallery, index);
      });
    });

    // Mobile: dot click navigates to slide
    dots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        var index = parseInt(dot.dataset.index, 10);
        goToSlide(gallery, index);
      });
    });

    // Mobile: track scroll position to update dots
    if (slidesContainer) {
      var scrollTimeout;
      slidesContainer.addEventListener('scroll', function() {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function() {
          updateDotsFromScroll(gallery, slidesContainer);
        }, 50);
      }, { passive: true });
    }

    // Listen for variant image changes from theme.js
    var pdpContainer = gallery.closest('[data-section-id]');
    if (pdpContainer) {
      // theme.js dispatches variantImageChange on the product container
      pdpContainer.addEventListener('variantImageChange', function(evt) {
        var variant = evt.detail && evt.detail.variant;
        if (!variant || !variant.featured_image) return;

        var mediaId = variant.featured_image.id;
        var targetSlide = gallery.querySelector('[data-v4-slide][data-media-id="' + mediaId + '"]');
        if (targetSlide) {
          var index = parseInt(targetSlide.dataset.index, 10);
          goToSlide(gallery, index);
        }
      });
    }
  }

  function goToSlide(gallery, index) {
    var slides = gallery.querySelectorAll('[data-v4-slide]');
    var thumbs = gallery.querySelectorAll('[data-v4-thumb]');
    var dots = gallery.querySelectorAll('[data-v4-dot]');
    var slidesContainer = gallery.querySelector('[data-v4-gallery-slides]');
    var isMobile = window.innerWidth < 750;

    if (isMobile && slidesContainer) {
      // Mobile: scroll to the slide position
      var targetSlide = slides[index];
      if (targetSlide) {
        slidesContainer.scrollTo({
          left: targetSlide.offsetLeft - slidesContainer.offsetLeft,
          behavior: 'smooth'
        });
      }
    } else {
      // Desktop: show/hide slides
      slides.forEach(function(slide, i) {
        if (i === index) {
          slide.classList.add('is-active');
          slide.removeAttribute('aria-hidden');
        } else {
          slide.classList.remove('is-active');
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }

    // Update thumbnails
    thumbs.forEach(function(thumb, i) {
      if (i === index) {
        thumb.classList.add('is-active');
      } else {
        thumb.classList.remove('is-active');
      }
    });

    // Update dots
    dots.forEach(function(dot, i) {
      if (i === index) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }

  function updateDotsFromScroll(gallery, container) {
    var slides = gallery.querySelectorAll('[data-v4-slide]');
    var dots = gallery.querySelectorAll('[data-v4-dot]');
    if (!slides.length || !dots.length) return;

    var scrollLeft = container.scrollLeft;
    var containerLeft = container.offsetLeft;
    var closestIndex = 0;
    var closestDist = Infinity;

    slides.forEach(function(slide, i) {
      var dist = Math.abs(slide.offsetLeft - containerLeft - scrollLeft);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });

    dots.forEach(function(dot, i) {
      if (i === closestIndex) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });

    // Also update thumbs to stay in sync
    var thumbs = gallery.querySelectorAll('[data-v4-thumb]');
    thumbs.forEach(function(thumb, i) {
      if (i === closestIndex) {
        thumb.classList.add('is-active');
      } else {
        thumb.classList.remove('is-active');
      }
    });
  }


  /* ==========================================================
     2. COLOR SWATCH VISUAL STATE
     The radio input change is picked up by theme.js for variant
     switching. We just need to update the visual .is-selected
     class on the swatch cards.
     ========================================================== */

  function initSwatches() {
    var swatchFieldsets = document.querySelectorAll('.v4-swatches');
    swatchFieldsets.forEach(function(fieldset) {
      var section = getV4Section(fieldset);
      var inputs = fieldset.querySelectorAll('.v4-swatch__input');
      inputs.forEach(function(input) {
        input.addEventListener('change', function() {
          // Update visual state for all swatches in this fieldset
          var swatches = fieldset.querySelectorAll('.v4-swatch');
          swatches.forEach(function(swatch) {
            swatch.classList.remove('is-selected');
          });
          // Find the parent .v4-swatch of the checked input
          var parentSwatch = input.closest('.v4-swatch');
          if (parentSwatch) {
            parentSwatch.classList.add('is-selected');
          }

          // Update the color label text — scoped to this section only
          if (section) {
            var colorLabel = section.querySelector('[data-variant-color-label]');
            if (colorLabel) {
              colorLabel.textContent = input.value;
            }
          }

          // Switch gallery image to match selected color (scoped)
          switchGalleryToColor(section, input.value);

          // Refresh sold-out state on the size dropdown for the new color
          refreshSizeAvailability(section);
        });
      });
    });
  }

  /**
   * Switch the V4 gallery to show the image for a given color value.
   * Reads the color→mediaId JSON map embedded in the page, then
   * finds the matching slide and navigates to it.
   */
  function switchGalleryToColor(section, colorValue) {
    var root = section || document;
    var mapEl = root.querySelector('[data-v4-color-media-map]');
    if (!mapEl) return;

    try {
      var colorMap = JSON.parse(mapEl.textContent);
    } catch (e) {
      return;
    }

    var mediaId = colorMap[colorValue];
    if (!mediaId) return;

    var gallery = root.querySelector('[data-v4-gallery]');
    if (!gallery) return;

    var targetSlide = gallery.querySelector('[data-v4-slide][data-media-id="' + mediaId + '"]');
    if (targetSlide) {
      var index = parseInt(targetSlide.dataset.index, 10);
      goToSlide(gallery, index);
    }
  }

  /**
   * Read the V4 section's product variants JSON.
   */
  function getSectionVariants(section) {
    if (!section) return null;
    var variantsEl = section.querySelector('[data-variant-json]');
    if (!variantsEl) return null;
    try {
      return JSON.parse(variantsEl.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * Build a {option1, option2, option3} map from the current UI state
   * (checked color radio + visible size select). Used both to refresh
   * sold-out state on color change AND to validate the variant at
   * submit time independent of theme.js.
   */
  function getSelectedOptionsFromUI(section) {
    if (!section) return {};
    var selected = {};
    // Color radio (any v4-swatch__input that's :checked)
    var colorRadio = section.querySelector('.v4-swatch__input:checked');
    if (colorRadio) {
      var idx = colorRadio.getAttribute('data-index'); // "option1" / "option2" / "option3"
      if (idx) selected[idx] = colorRadio.value;
    }
    // Any non-color [data-variant-input] selects (size, material, etc)
    var selects = section.querySelectorAll('select[data-variant-input]');
    Array.prototype.forEach.call(selects, function(sel) {
      var idx = sel.getAttribute('data-index');
      if (idx) selected[idx] = sel.value;
    });
    return selected;
  }

  /**
   * Find the variant matching the current UI selection.
   * Returns the variant object or null.
   */
  function findVariantFromSelection(variants, selected) {
    if (!variants || !selected) return null;
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (selected.option1 != null && v.option1 !== selected.option1) continue;
      if (selected.option2 != null && v.option2 !== selected.option2) continue;
      if (selected.option3 != null && v.option3 !== selected.option3) continue;
      return v;
    }
    return null;
  }

  /**
   * Update each size option's text to reflect availability with the
   * currently-selected color. Sold-out sizes get a " — Sold out" suffix
   * but stay SELECTABLE — picking one flips the ATC button to "Sold out".
   *
   * The color option position is read from the checked swatch's
   * data-index ("option1"/"option2"/"option3") so it works regardless
   * of which option position the color sits at.
   */
  function refreshSizeAvailability(section) {
    if (!section) return;
    var sizeSelect = section.querySelector('[data-v4-size-select]');
    var variants = getSectionVariants(section);
    if (!sizeSelect || !variants) return;

    var checkedSwatch = section.querySelector('.v4-swatch__input:checked');
    if (!checkedSwatch) return;

    var colorKey = checkedSwatch.getAttribute('data-index'); // "option1" / 2 / 3
    var currentColor = checkedSwatch.value;
    if (!colorKey || !currentColor) return;

    var sizePosition = parseInt(sizeSelect.getAttribute('data-option-position') || '2', 10);
    var sizeKey = 'option' + sizePosition;

    Array.prototype.forEach.call(sizeSelect.options, function(opt) {
      var sizeValue = opt.value;
      var available = variants.some(function(v) {
        return v.available && v[colorKey] === currentColor && v[sizeKey] === sizeValue;
      });
      var baseLabel = opt.getAttribute('data-base-label') || sizeValue;
      opt.textContent = available ? baseLabel : (baseLabel + ' — ' + V4_STRINGS.soldOut);
      // Options remain selectable — ATC button reflects sold-out state via updateV4AtcState.
      opt.disabled = false;
    });

    // …and the other way round: the colours that are sold out in the chosen size.
    refreshColorAvailability(section);
  }

  /**
   * Mark every colour that is sold out in the chosen size: the card fades and gets a "Slut i S" label on its
   * top edge (Marcus, 2026-10-06, the same as Storlekskollen). The colour stays selectable; the ATC button
   * then says "Slut i lager" (updateV4AtcState), as for a sold-out size. Screen readers hear it from a
   * visually hidden text inside the label. Runs wherever the size availability is refreshed.
   */
  function refreshColorAvailability(section) {
    if (!section) return;
    var sizeSelect = section.querySelector('[data-v4-size-select]');
    var variants = getSectionVariants(section);
    if (!sizeSelect || !variants) return;

    var sizePosition = parseInt(sizeSelect.getAttribute('data-option-position') || '2', 10);
    var sizeKey = 'option' + sizePosition;
    var sizeValue = sizeSelect.value;
    // "S: 60cm (0 - 5kg)" → "S"
    var sizeShort = String(sizeValue || '').split(':')[0].trim();

    Array.prototype.forEach.call(section.querySelectorAll('.v4-swatch'), function(swatch) {
      var input = swatch.querySelector('.v4-swatch__input');
      if (!input) return;
      var colorKey = input.getAttribute('data-index');
      var available = variants.some(function(v) {
        return v.available && v[colorKey] === input.value && v[sizeKey] === sizeValue;
      });
      swatch.classList.toggle('is-soldout', !available);

      var badge = swatch.querySelector('.v4-swatch__soldout');
      var label = swatch.querySelector('.v4-swatch__label');
      var sr = label && label.querySelector('.v4-swatch__soldout-sr');
      if (!available) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'v4-swatch__soldout';
          badge.setAttribute('aria-hidden', 'true');
          swatch.appendChild(badge);
        }
        badge.textContent = 'Udsolgt i ' + sizeShort;
        if (label && !sr) {
          sr = document.createElement('span');
          sr.className = 'v4-swatch__soldout-sr visually-hidden';
          label.appendChild(sr);
        }
        if (sr) sr.textContent = ', udsolgt i ' + sizeShort;
      } else {
        if (badge) badge.parentNode.removeChild(badge);
        if (sr) sr.parentNode.removeChild(sr);
      }
    });
  }


  /* ==========================================================
     2b. SELF-CONTAINED VARIANT CHANGE HANDLER
     theme.Product (in theme.js) crashes when constructing on the
     V4 buy box because it expects [data-product-photos] which V4
     doesn't have. theme.Sections.register catches that error
     silently — meaning theme.js's variant change listeners are
     never attached to V4 buy boxes. We handle everything ourselves.
     ========================================================== */

  function formatMoneySafe(cents) {
    if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
      var fmt = theme.settings && theme.settings.moneyFormat;
      return theme.Currency.formatMoney(cents, fmt);
    }
    return (cents / 100).toFixed(2);
  }

  function updateV4Price(section, variant) {
    if (!variant) return;
    var priceEl = section.querySelector('[data-product-price]');
    if (priceEl) {
      priceEl.innerHTML = formatMoneySafe(variant.price);
    }
    var compareEl = section.querySelector('[data-compare-price]');
    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.innerHTML = formatMoneySafe(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }
  }

  // Free-shipping threshold in öre — matches snippets/cart-drawer.liquid (50000 = 500 kr).
  var V4_FREE_SHIP_ORE = 50000;

  /**
   * Probiotics-only save badge. Mirrors the SELECTED bundle option's savings
   * (e.g. "SPARA 120 KR") into the price-row badge, appends "+ FRI FRAKT" only
   * when the variant is >= 500 kr, and hides the badge when there's no saving
   * (e.g. "1 burk"). The option->savings map is rendered from the bundle
   * settings (data-save-map), so the badge always agrees with the bundle cards.
   *
   * No-ops on every product that does NOT render [data-save-dynamic] — i.e.
   * every product except probiotics keeps its existing static % badge untouched.
   */
  function updateSaveBadge(section, variant) {
    var badge = section.querySelector('[data-save-badge][data-save-dynamic]');
    if (!badge) return; // not the dynamic (probiotics) badge -> leave everything as-is
    if (!variant) { badge.style.display = 'none'; badge.textContent = ''; return; }

    var map = {};
    try { map = JSON.parse(badge.getAttribute('data-save-map') || '{}'); } catch (e) { map = {}; }

    var key = variant.option1 || variant.title || '';
    var saveText = (map[key] || '').toString().trim();
    if (!saveText && key) { saveText = (map[key.trim()] || '').toString().trim(); }

    if (!saveText) { badge.style.display = 'none'; badge.textContent = ''; return; }

    if (variant.price >= V4_FREE_SHIP_ORE) {
      saveText = saveText + ' + FRI FRAGT';
    }
    badge.textContent = saveText;
    badge.style.display = '';
  }

  function updateV4MasterSelect(section, variant) {
    var master = section.querySelector('[data-product-select]');
    if (master && master.value !== String(variant.id)) {
      master.value = String(variant.id);
      // Dispatch change so listeners (Shop Pay, Appikon Back in Stock,
      // Klaviyo BIS, etc.) that hook document-level 'change' events on
      // [name="id"] can detect the variant switch and update their UI.
      // Mirrors the Motion theme's _updateMasterSelect behavior.
      master.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Navigate the V4 gallery to the variant's featured image.
   * Falls back to the color → media map if featured_image is missing.
   */
  function switchGalleryToVariant(section, variant) {
    if (!variant) return;
    var gallery = section.querySelector('[data-v4-gallery]');
    if (!gallery) return;

    var mediaId = null;
    if (variant.featured_media && variant.featured_media.id) {
      mediaId = variant.featured_media.id;
    } else if (variant.featured_image && variant.featured_image.id) {
      // featured_image.id is the image media id in modern Shopify
      mediaId = variant.featured_image.id;
    }

    if (!mediaId) return;

    var targetSlide = gallery.querySelector('[data-v4-slide][data-media-id="' + mediaId + '"]');
    if (targetSlide) {
      var index = parseInt(targetSlide.dataset.index, 10);
      goToSlide(gallery, index);
    }
  }

  // Swedish strings — hardcoded per product requirements.
  var V4_STRINGS = {
    addToCart: 'Læg i indkøbskurv',
    soldOut: 'Udsolgt'
  };

  /**
   * Update only the ATC button label (+ price).
   * Does NOT touch disabled state, so the server-rendered
   * disabled attribute (and any back-in-stock app hooking
   * onto it) is preserved.
   */
  function updateV4AtcText(section, variant) {
    var btn = section.querySelector('[data-add-to-cart]');
    if (!btn) return;
    var textEl = btn.querySelector('[data-add-to-cart-text]') || btn;
    if (!variant) return;

    if (variant.available) {
      textEl.textContent = V4_STRINGS.addToCart + ' - ' + formatMoneySafe(variant.price);
    } else {
      textEl.textContent = V4_STRINGS.soldOut;
    }
  }

  /**
   * Full state update — used when the user interacts with a variant
   * picker. Toggles both text AND disabled state.
   */
  function updateV4AtcState(section, variant) {
    var btn = section.querySelector('[data-add-to-cart]');
    if (!btn) return;
    var textEl = btn.querySelector('[data-add-to-cart-text]') || btn;

    if (variant && variant.available) {
      btn.disabled = false;
      btn.classList.remove('btn--sold-out');
      textEl.textContent = V4_STRINGS.addToCart + ' - ' + formatMoneySafe(variant.price);
    } else {
      btn.disabled = true;
      btn.classList.add('btn--sold-out');
      textEl.textContent = V4_STRINGS.soldOut;
    }
  }

  /**
   * Compute matching variant from current UI state and update everything:
   * price, master select, gallery image, ATC button, sold-out availability.
   */
  function syncV4Variant(section, srcInput) {
    if (!section) return;
    var variants = getSectionVariants(section);
    if (!variants) return;

    var selected = getSelectedOptionsFromUI(section);
    var variant = findVariantFromSelection(variants, selected);
    if (!variant) return;

    updateV4MasterSelect(section, variant);
    updateV4Price(section, variant);
    updateSaveBadge(section, variant);
    switchGalleryToVariant(section, variant);
    updateV4AtcState(section, variant);

    // Refresh visible labels above each option selector.
    var colorLabel = section.querySelector('[data-variant-color-label]');
    if (colorLabel) {
      var checkedColor = section.querySelector('.v4-swatch__input:checked');
      if (checkedColor) colorLabel.textContent = checkedColor.value;
    }
    var sizeLabel = section.querySelector('[data-variant-size-label]');
    var sizeSel = section.querySelector('[data-v4-size-select]');
    if (sizeLabel && sizeSel) {
      var sizeOpt = sizeSel.options[sizeSel.selectedIndex];
      if (sizeOpt) {
        sizeLabel.textContent = sizeOpt.getAttribute('data-base-label') || sizeOpt.value;
      }
    }

    // Refresh sold-out labels on size options for the current color.
    refreshSizeAvailability(section);
  }

  /**
   * After the size recommender (psr-tools.js) updates the .size-value
   * element with a recommended size code (e.g. " M ", " XL "), find
   * the matching option in the V4 size <select> and select it. Each
   * size option's text starts with the code (e.g. "M: 70cm (5–10kg)").
   */
  function initSizeRecommenderAutoSelect() {
    getAllV4Sections().forEach(function(section) {
      var breedSelect = section.querySelector('#SingleOptionBreed');
      var sizeValueEl = section.querySelector('.size-value');
      var sizeSelect = section.querySelector('[data-v4-size-select]');
      if (!breedSelect || !sizeValueEl || !sizeSelect) return;

      breedSelect.addEventListener('change', function() {
        // psr-tools.js updates .size-value synchronously in its handler.
        // Defer one tick to make sure that has run regardless of
        // listener registration order.
        setTimeout(function() {
          var recommended = (sizeValueEl.textContent || '').trim().toUpperCase();
          if (!recommended) return;

          var matched = null;
          Array.prototype.forEach.call(sizeSelect.options, function(opt) {
            if (matched) return;
            // Match against the option text's leading size code.
            // e.g. "M: 70cm (5–10kg)" → code "M"
            var optText = (opt.textContent || '').trim().toUpperCase();
            var optCode = optText.split(':')[0].trim().split(/\s/)[0];
            if (optCode === recommended) {
              matched = opt;
            }
          });

          if (matched && !matched.disabled && sizeSelect.value !== matched.value) {
            sizeSelect.value = matched.value;
            sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }, 0);
      });
    });
  }

  function initV4VariantSync() {
    getAllV4Sections().forEach(function(section) {
      // Delegated change listener — catches both swatch radios and size select.
      section.addEventListener('change', function(e) {
        var input = e.target;
        if (!input || !input.matches) return;
        if (!input.matches('[data-variant-input]')) return;
        syncV4Variant(section, input);
      });
    });
  }


  /* ==========================================================
     3. CART FORM — Intercept V4 form submission
     theme.js's AjaxProduct doesn't initialize on the V4 form
     (different template structure), so we handle AJAX add-to-cart
     ourselves and dispatch 'ajaxProduct:added' — the event that
     theme.CartDrawer listens for to rebuild cart + open drawer.
     ========================================================== */

  function initCartForm() {
    // Hook EVERY V4 form on the page (modals, quick-views, etc).
    var v4Forms = document.querySelectorAll('form[id^="AddToCartForm-V4"]');
    if (!v4Forms.length) return;

    v4Forms.forEach(function(v4Form) {
      attachV4FormHandler(v4Form);
    });
  }

  function attachV4FormHandler(v4Form) {
    v4Form.addEventListener('submit', function(e) {
      e.preventDefault();
      e.stopPropagation();

      var form = v4Form;
      var submitBtn = form.querySelector('[data-add-to-cart]');

      // Force-sync the master select to whatever the current UI state
      // resolves to. The ATC button is already disabled when the variant
      // is sold-out (via updateV4AtcState), so we don't need a JS alert
      // here — the button itself blocks submission. This matches how
      // the live theme behaves.
      var section = getV4Section(form);
      var idInput = form.querySelector('[data-product-select]');
      var allVariants = getSectionVariants(section);
      if (allVariants && section && idInput) {
        var selected = getSelectedOptionsFromUI(section);
        var resolved = findVariantFromSelection(allVariants, selected);
        if (resolved && idInput.value !== String(resolved.id)) {
          idInput.value = String(resolved.id);
        }
      }

      // Loading state
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('btn--loading');
      }

      var data = new URLSearchParams(new FormData(form)).toString();

      // Prefer theme.routes.cartAdd if defined; fall back to /cart/add.js.
      // Guarding on both prevents `fetch(undefined)` if theme exposes
      // theme.routes but not routes.cartAdd (e.g. older theme builds).
      var cartAddUrl = (typeof theme !== 'undefined' && theme.routes && theme.routes.cartAdd) || '/cart/add.js';
      fetch(cartAddUrl, {
        method: 'POST',
        body: data,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(function(response) { return response.json(); })
      .then(function(product) {
        if (product.status === 422) {
          // Error (e.g. sold out)
          var errMsg = product.description || 'Could not add to cart';
          alert(errMsg);
        } else {
          // Dispatch the same event theme.CartDrawer listens for
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: product,
              addToCartBtn: submitBtn
            }
          }));
        }

        // Reset button
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('btn--loading');
        }
      })
      .catch(function(error) {
        console.error('V4 Add to Cart error:', error);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('btn--loading');
        }
      });
    });
  }


  /* ==========================================================
     4. UPSELL — Add complementary product to cart
     ========================================================== */

  function initUpsell() {
    // Init each upsell card independently (one per V4 section).
    var upsellCards = document.querySelectorAll('.v4-pdp__upsell');
    upsellCards.forEach(initSingleUpsell);
  }

  function initSingleUpsell(upsellRoot) {
    var section = getV4Section(upsellRoot);
    var colorDots = upsellRoot.querySelectorAll('[data-upsell-color]');
    var sizeSelect = upsellRoot.querySelector('[data-upsell-size]');
    var mainImage = upsellRoot.querySelector('[data-upsell-main-image]');
    var priceEl = upsellRoot.querySelector('[data-upsell-price]');
    var addBtn = upsellRoot.querySelector('[data-upsell-add]');

    // Parse the variant JSON embedded in the upsell card
    var variantsEl = upsellRoot.querySelector('[data-upsell-variants]');
    var variants = [];
    if (variantsEl) {
      try {
        variants = JSON.parse(variantsEl.textContent);
      } catch (e) {
        console.error('V4 upsell: failed to parse variants JSON', e);
      }
    }
    if (!variants.length) {
      // No variants means the upsell can't resolve color/size → abort init
      // so we don't silently half-wire a broken card.
      return;
    }

    // Preload all color-swatch images so clicking a swatch swaps instantly
    // instead of waiting 300–500 ms for a fresh CDN fetch. Only the initial
    // color image loads on render; the rest live in data-image-url. new
    // Image() triggers browser cache without adding DOM nodes. Wrapped in
    // requestIdleCallback so preloading doesn't compete with first paint.
    function preloadUpsellColorImages() {
      colorDots.forEach(function(dot) {
        var url = dot.getAttribute('data-image-url');
        if (!url) return;
        var img = new Image();
        img.src = url;
      });
    }
    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadUpsellColorImages, { timeout: 2000 });
    } else {
      setTimeout(preloadUpsellColorImages, 500);
    }

    // Helper: find variant by color + raw size
    function findVariant(color, rawSize) {
      for (var i = 0; i < variants.length; i++) {
        if (variants[i].option1 === color && variants[i].option2 === rawSize) {
          return variants[i];
        }
      }
      return null;
    }

    // Helper: get currently selected color (scoped to this upsell card)
    function getActiveColor() {
      var active = upsellRoot.querySelector('[data-upsell-color].is-active');
      return active ? active.getAttribute('data-color-value') : null;
    }

    // Helper: get currently selected raw size
    function getActiveRawSize() {
      if (!sizeSelect) return null;
      var opt = sizeSelect.options[sizeSelect.selectedIndex];
      return opt ? opt.getAttribute('data-size-raw') : null;
    }

    // Update price, image, and variant ID based on selected color + size
    function updateUpsell() {
      var color = getActiveColor();
      var rawSize = getActiveRawSize();
      if (!color || !rawSize) return;

      var variant = findVariant(color, rawSize);
      if (!variant) return;

      // Update price
      if (priceEl) {
        if (typeof theme !== 'undefined' && theme.Currency && theme.Currency.formatMoney) {
          priceEl.textContent = theme.Currency.formatMoney(variant.price, theme.settings && theme.settings.moneyFormat);
        } else {
          // Fallback: cents → currency-less number, log so devs notice.
          console.warn('V4 upsell: theme.Currency.formatMoney not available — price not formatted.');
          priceEl.textContent = (variant.price / 100).toFixed(2);
        }
      }

      // Update the select value to this variant's ID so add-to-cart uses it
      if (sizeSelect) {
        // Find the option with matching raw size and update its value
        for (var i = 0; i < sizeSelect.options.length; i++) {
          if (sizeSelect.options[i].getAttribute('data-size-raw') === rawSize) {
            sizeSelect.options[i].value = variant.id;
            break;
          }
        }
      }

      // Update image to match color (scoped)
      var activeDot = upsellRoot.querySelector('[data-upsell-color].is-active');
      var newSrc = activeDot ? activeDot.getAttribute('data-image-url') : null;
      if (newSrc && mainImage) {
        mainImage.src = newSrc;
      }
    }

    // Color swatch click
    colorDots.forEach(function(dot) {
      dot.addEventListener('click', function() {
        colorDots.forEach(function(d) { d.classList.remove('is-active'); });
        dot.classList.add('is-active');
        updateUpsell();
      });
    });

    // Size select change
    if (sizeSelect) {
      sizeSelect.addEventListener('change', function() {
        updateUpsell();
      });
    }

    // Sync cover size when main product size changes.
    // Match by leading size code (S/M/L/XL/XXL) rather than full
    // string, since main product and upsell use different
    // descriptors (e.g. "M: 70cm (5-10kg)" vs "M: 60cm (0-5kg)").
    function extractSizeCode(rawSize) {
      if (!rawSize) return '';
      return String(rawSize).split(':')[0].trim().toUpperCase();
    }

    function syncUpsellToMainSize() {
      var mainSizeSelect = section ? section.querySelector('[data-v4-size-select]') : null;
      if (!mainSizeSelect || !sizeSelect) return;
      var targetCode = extractSizeCode(mainSizeSelect.value);
      if (!targetCode) return;
      for (var i = 0; i < sizeSelect.options.length; i++) {
        var optCode = extractSizeCode(sizeSelect.options[i].getAttribute('data-size-raw'));
        if (optCode === targetCode) {
          if (sizeSelect.selectedIndex !== i) {
            sizeSelect.selectedIndex = i;
            updateUpsell();
          }
          break;
        }
      }
    }

    var mainSizeSelect = section ? section.querySelector('[data-v4-size-select]') : null;
    if (mainSizeSelect && sizeSelect) {
      mainSizeSelect.addEventListener('change', syncUpsellToMainSize);
      // Sync once on load too, so the upsell starts on the same size
      // as the main product (matters for breed-recommender pre-selects).
      syncUpsellToMainSize();
    }

    // Add button
    if (addBtn) {
      addBtn.addEventListener('click', function() {
        var variantId = sizeSelect ? sizeSelect.value : null;
        if (!variantId) return;

        addBtn.disabled = true;
        var originalText = addBtn.textContent;
        addBtn.textContent = 'Tilføjer…';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: parseInt(variantId, 10),
            quantity: 1
          })
        })
        .then(function(response) {
          return response.json();
        })
        .then(function(product) {
          if (product.status === 422) {
            alert(product.description || 'Kunne ikke lægges i indkøbskurven');
            addBtn.textContent = originalText;
            addBtn.disabled = false;
            return;
          }
          // Dispatch the same event theme.CartDrawer listens for
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: product, addToCartBtn: addBtn }
          }));
          addBtn.textContent = 'Tilføjet ✓';
          setTimeout(function() {
            addBtn.textContent = originalText;
            addBtn.disabled = false;
          }, 1500);
        })
        .catch(function(error) {
          console.error('Upsell add error:', error);
          addBtn.textContent = originalText;
          addBtn.disabled = false;
        });
      });
    }
  }


  /* ==========================================================
     4b. ACCORDION DEEP-LINK BUTTONS
     Buttons with [data-v4-open-accordion="<target>"] open the
     matching <details> accordion (data-target="<target>") in
     the same V4 section, then scroll it into view.
     ========================================================== */

  function initAccordionOpeners() {
    document.querySelectorAll('[data-v4-open-accordion]').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var target = btn.getAttribute('data-v4-open-accordion');
        var section = getV4Section(btn) || document;
        var summary = section.querySelector('.v4-pdp__accordion-btn[data-target="' + target + '"]');
        if (!summary) return;
        var details = summary.closest('details');
        if (details && !details.open) {
          details.open = true;
        }
        summary.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });
  }


  /* ==========================================================
     4c. SIZE GUIDE MODAL
     Any element with [data-v4-open-size-guide] opens the modal
     in the same .v4-pdp section. Closed by X, overlay click, or Esc.
     ========================================================== */

  function openSizeGuide(modal) {
    if (!modal) return;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // Focus the panel so keyboard users land inside the modal.
    var panel = modal.querySelector('.v4-size-guide__panel');
    if (panel) panel.focus();
  }

  function closeSizeGuide(modal) {
    if (!modal) return;
    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function initSizeGuide() {
    // Open triggers — delegated so it catches every v4-pdp section's
    // "How to Measure" button and "Size Guide" accordion row.
    document.addEventListener('click', function(e) {
      var trigger = e.target && e.target.closest && e.target.closest('[data-v4-open-size-guide]');
      if (!trigger) return;
      e.preventDefault();
      var section = getV4Section(trigger);
      if (!section) return;
      var modal = section.querySelector('[data-v4-size-guide]');
      openSizeGuide(modal);
    });

    // Close triggers (X button + overlay click)
    document.addEventListener('click', function(e) {
      var closeBtn = e.target && e.target.closest && e.target.closest('[data-v4-size-guide-close]');
      if (!closeBtn) return;
      var modal = closeBtn.closest('[data-v4-size-guide]');
      closeSizeGuide(modal);
    });

    // Escape key closes any open size guide modal
    document.addEventListener('keydown', function(e) {
      if (e.key !== 'Escape') return;
      var openModals = document.querySelectorAll('[data-v4-size-guide]:not([hidden])');
      Array.prototype.forEach.call(openModals, closeSizeGuide);
    });
  }


  /* ==========================================================
     5. CART DRAWER
     Cart drawer is handled by theme.CartDrawer which listens
     for the 'ajaxProduct:added' event. Both initCartForm() and
     initUpsell() dispatch this event after successful AJAX add.
     No custom drawer logic needed here.
     ========================================================== */


  /* ==========================================================
     6. MOBILE GALLERY INIT
     On mobile, override desktop show/hide to enable scroll.
     Must run after DOM is ready AND on resize.
     ========================================================== */

  function initMobileGallery() {
    var isMobile = window.innerWidth < 750;
    var slides = document.querySelectorAll('[data-v4-slide]');

    if (isMobile) {
      // On mobile, make all slides visible for horizontal scroll
      slides.forEach(function(slide) {
        slide.style.display = 'block';
        slide.removeAttribute('aria-hidden');
      });
    } else {
      // On desktop, restore show/hide behavior
      slides.forEach(function(slide) {
        if (slide.classList.contains('is-active')) {
          slide.style.display = '';
          slide.removeAttribute('aria-hidden');
        } else {
          slide.style.display = '';
          slide.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }

  // Debounced resize handler
  var resizeTimeout;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(initMobileGallery, 150);
  });


  /* ==========================================================
     INIT — Run everything when DOM is ready
     ========================================================== */

  function init() {
    initGallery();
    initSwatches();
    initV4VariantSync();
    initSizeRecommenderAutoSelect();
    initCartForm();
    initUpsell();
    initAccordionOpeners();
    initSizeGuide();
    initMobileGallery();

    // Initial sync so the size dropdown sold-out labels reflect the
    // current color, and the ATC button shows the price. We use the
    // text-only updater here (not the full state updater) so we don't
    // clobber the server-rendered disabled attribute — which matters
    // for back-in-stock apps that hook onto it.
    getAllV4Sections().forEach(function(section) {
      refreshSizeAvailability(section);
      var variants = getSectionVariants(section);
      if (!variants) return;
      var selected = getSelectedOptionsFromUI(section);
      var variant = findVariantFromSelection(variants, selected);
      if (variant) {
        updateV4AtcText(section, variant);
        updateSaveBadge(section, variant);
      }
    });
  }

  // Support both DOMContentLoaded and already-loaded states
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

